import * as React from 'react'
import { Row, Col, Badge, InputGroup, InputGroupText, Input} from 'reactstrap';
import { BadgeContainer, Badge as Badge2 } from '@progress/kendo-react-indicators';
import { EditorUtils, Editor, EditorTools } from '@progress/kendo-react-editor'
import { LuBellRing, LuBellOff, LuLoaderCircle } from 'react-icons/lu';
import { Button } from "@progress/kendo-react-buttons";
import { marks, nodes, Schema } from '@progress/kendo-editor-common';
import { accentUtils, t, formHelper, showOK, promise, showDialog, A, from, format } from '../services/HelperService'
import { AccentSpinner } from './AccentSpinner'
import { AccentSectionHeader } from './AccentSectionHeader'
import AccentIcons from '../icons/Icons'
import { SelectSketchDlg } from '../dialogs/SelectSketchDlg'
import { queryNoTracking, update } from '../services/DataService'
import { getAccess, getUser } from '../services/UserService';
import { RTLSearchSelectCtrl } from './RTLSearchSelectCtrl';
import GRIDS from '../grids/_GridDefinitions';
import { AccentPopup } from '../dialogs/AccentPopup';
import { AlertsDisplay, useFileNoteAlerts } from '../app-framework/FileNoteAlerts';


const querySketch = queryNoTracking("Sketch", 0);
const query_Representative_ActiveGrid = queryNoTracking("Representative_ActiveGrid");
const query_FileNoteAlertsForContactCompany = queryNoTracking("FileNoteAlertsForContactCompany", 0);

function canEditFileNote(fileNote) {

    var user = getUser();

    if (fileNote.SystemGenerated) return false;

    if (user.ID === fileNote.RepresentativeID) return true;

    if (getAccess().features().FeatureAllowSetup()) return true;

    if (getAccess().features().FeatureAllowManagement()) return true;

    
    return false;
}


const FileNoteTag = React.memo(props => {

    if (accentUtils.isNullOrEmpty(props.tagName)) return null;

    return <Badge style={{ marginRight: '5px', verticalAlign: 'top' }} color="primary">{t(props.tagName)}</Badge>;
});

const InsertSketch = ({ view, jobID, jobLineID, defaultSketchName, done, onInsert }) => {



    const imageNodeType = view && view.state.schema.nodes.image;
    const canInsert = view && EditorUtils.canInsert(view.state, imageNodeType);

    const handleInsert = React.useCallback(async () => {
        try {
            const selectResult = await showDialog(
                <SelectSketchDlg jobID={jobID} jobLineID={jobLineID} defaultSketchName={defaultSketchName} />
            );

            if (selectResult.canceled || accentUtils.isNull(selectResult.selected)) return;

            const data = await querySketch.getFirstOrDefaultWithCache({ id: selectResult.selected.ID });

            if (accentUtils.isEmpty(data.Sketch.Image)) return;

            const attrs = {
                src: `data:image/png;base64,${data.Sketch.Image}`,
                "data-accent-doc": data.Document.ID
            };

            onInsert(attrs);

            if (done) done();
        } catch (error) {
            console.error("Error inserting image:", error);
        }
    }, [jobID, jobLineID, defaultSketchName, done, onInsert]);


    if (accentUtils.isNull(jobID) && accentUtils.isNull(jobLineID)) return null;

    return (
        <Button
            onClick={handleInsert}
            disabled={!canInsert}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title="Insert sketch"
            icon="image-insert"
        />
    );
};

const InsertUser = ({ jobID,done, onInsert }) => {




    const handleInsert = React.useCallback(async () => {
        try {

            const result = await showDialog(<RTLSearchSelectCtrl
                title="Insert User"
                closeTitle="application_strings.application.buttons.insert"
                searchPlaceholder="Search User"
                gridID="79664FC3-5B21-4409-B2F9-74F7CE760603"
                query={s => query_Representative_ActiveGrid.getAllAsQueryableWithCache({ search: s })}
                columns={GRIDS.Representative_Grid.Default}
                runQueryOnShow                
                singleSelection
            />);
            
            
            const hasValue = !result.canceled && result.selection && result.selection.length > 0;

            if (!hasValue) return;

            const value = from(result.selection).first();

            onInsert({ user:  value, jobID: jobID });

            if (done) done();
        } catch (error) {
            console.error("Error inserting user:", error);
        }
    }, [jobID, done, onInsert]);

    return (
        <Button
            onClick={handleInsert}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title="Insert User"
            icon="user"
        />
    );
};


const FileNoteView = ({ fileNote }) => {

    const user = getUser();
    const elementRef = React.useRef(null);
    const ctrl = React.useRef(null);
    const [hasBeenViewed, setHasBeenViewed] = React.useState(from(fileNote.Views).any(v=>v.ID === user.ID));

    const viewCount = fileNote.Views?.length ?? 0;


    const [popoverOpen, setPopoverOpen] = React.useState(false);



    const toggle = () => {
        setPopoverOpen(true);
    };

    const close = () => {
        setPopoverOpen(false);
    };


    React.useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !hasBeenViewed) {
                // Call server API to mark the item as viewed by the user                
                setHasBeenViewed(true);
                update("FileNote_AddView", { ParentID: fileNote.ID, ID: user.ID });
            }
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 1.0
        });

        if (elementRef.current) {
            observer.observe(elementRef.current);
        }

        return () => {
            if (elementRef.current) {
                observer.unobserve(elementRef.current);
            }
        };
    }, [hasBeenViewed]);

    const content = (
        <div>

            {from(fileNote.Views).orderBy(v=>new Date(v.Date)).toArray().map((view, index) => (
                <div className="d-flex" key={index}>
                    <div>{format.formatDateTime(view.Date)}</div>
                    <div style={{ paddingLeft: "5px", paddingRight: "5px" }}>-</div>
                    <div>{view.FullName}</div>
                </div>

            ))}
        </div>
    );

    return <div ref={elementRef} style={{display: "inline-block"}}>
        <BadgeContainer >
            <AccentIcons.Eye ref={ctrl} style={{ color: "white", border: "1px solid black", borderRadius: "3px", backgroundColor: "#335fd9" }} onClick={toggle} top={7} />
            <Badge2
                position="inside"
                themeColor="success" align={{
                    vertical: "top",
                    horizontal: "end",
                }}>{viewCount}</Badge2>
        </BadgeContainer>
        <AccentPopup
            className="jobWatcherPopup"
            popupAlign={{ horizontal: "left" }}
            anchorAlign={{ horizontal: "right", vertical: "bottom" }}
            show={popoverOpen}
            onAutoClose={close}
            anchor={ctrl}
            headerContent={
                <div className="d-flex">
                    <div className="me-auto"><strong>VIEWED THIS NOTE</strong></div>
                    <div><AccentIcons.Close className="acc-btn" onClick={close} noPadding /></div>
                </div>
            }
        >
            {content}
        </AccentPopup>
    </div>;
};

export class FileNoteCtrl extends React.Component {

    constructor(props) {
        super(props);
        
        this.editor = React.createRef();

        this.state = {
            loaded: false,
            editNote: null,
            count: null,
            skip: 0,
            search: null,
            isAddingNote: false
        };

        this.addWatchUsers = [];
        this.lastThreeChars = null;

        this.searchRefreshKey = 1;

        this.scrollToViewWithOffset = this.scrollToViewWithOffset.bind(this);
//        this.onHtmlChange = this.onHtmlChange.bind(this);
        this.onHeaderExpanded = this.onHeaderExpanded.bind(this);
        this.refreshNotes = this.refreshNotes.bind(this);
        this.addNewNote = this.addNewNote.bind(this);
        this.addNote = this.addNote.bind(this);
        this.allowSketchInsert = this.allowSketchInsert.bind(this);
        this.componentDidMount = this.componentDidMount.bind(this);
        this.onEdit = this.onEdit.bind(this);
        this.onEmail = this.onEmail.bind(this);
        this.saveEditNote = this.saveEditNote.bind(this);
        this.undoEditNote = this.undoEditNote.bind(this);
        this.onInsertSketch = this.onInsertSketch.bind(this);
        this.onInsertUser = this.onInsertUser.bind(this);
        this.onSeachChange = this.onSeachChange.bind(this);
        this.onKeyPress = this.onKeyPress.bind(this);
    }

    componentDidMount() {
        if (this.props.noHeaderSection) {
            this.onHeaderExpanded();
        }
    }

    onKeyPress(event){

        if (event.charCode === 13) {
            this.refreshNotes();
        }
    };

    onSeachChange(value) {

        if (accentUtils.isNull(value)) {
            this.searchRefreshKey++;
        }

        this.setState({ search: value }, () => {
            if (accentUtils.isNull(value)) {
                this.refreshNotes();
            }
        });


    }

    allowSketchInsert() {
        return !accentUtils.isEmpty(this.props.jobID) || !accentUtils.isEmpty(this.props.jobLineID)
    }

    addNewNote(note) {
        const res = this.props.addNote(note, [...this.addWatchUsers]);

        this.addWatchUsers = [];

        return res;
    }

    onEmail(fileNote) {
        showDialog(<RTLSearchSelectCtrl
            title="Email File Note To"
            closeTitle="application_strings.application.buttons.send"
            searchPlaceholder="Search User"
            gridID="79664FC3-5B21-4409-B2F9-74F7CE760603"
            query={s => query_Representative_ActiveGrid.getAllAsQueryableWithCache({ search: s })}
            columns={GRIDS.Representative_Grid.Default}
            runQueryOnShow                        
        />).then(e => {

            if (!e.canceled && e.selection && e.selection.length > 0) {
                update("Notifications_EmailTo", {
                    ParentID: fileNote.ID,
                    IDs: from(e.selection).select(i => i.ID).toArray()
                }, true).then(r => {          
                    showOK("Emails Sent", "Emails Sent", ["application_strings.application.buttons.close"], false, null, true, 600);
                });
            }
        });

    }

    onEdit(e) {

        this.setState({ editNote: e }, () => {
            const view = this.editor.current.view;
            EditorUtils.setHtml(view, e.Notes);

        });
    }
    
    async saveEditNote() {


        const view = this.editor.current.view;
        const html = EditorUtils.getHtml(view.state);



        const res = await update("UpdateFileNote", { ID: this.state.editNote.ID, Notes: html, AddWatchUsers: [...this.addWatchUsers] }, true);

        this.addWatchUsers = [];
        EditorUtils.setHtml(view, "");

        this.setState({ editNote: null });
        this.refreshNotes();
    }

    onInsertSketch(e) {
        
        const view = this.editor.current.view;
        const imageNodeType = view && view.state.schema.nodes.image;

        if (view.state && view.dispatch) {
            const imageNode = imageNodeType.createAndFill(e);
            const tr = view.state.tr;
            tr.replaceSelectionWith(imageNode);
            view.dispatch(tr);
        } else {
            console.error('Transaction did not change the document');
        }
    }

    onInsertUser(e) {

        const view = this.editor.current.view;

        if (view.state && view.dispatch) {
            const tr = view.state.tr;
            tr.insertText(e.user.FullName, view.state.selection.from);
            view.dispatch(tr);
            this.addWatchUsers.push(e.user.ID);
        }
    }


    undoEditNote() {
        this.setState({ editNote: null }, () => {
            const view = this.editor.current.view;
            EditorUtils.setHtml(view, "<p></p>");
        });
    }

    addNote(e) {


        if (this.editor.current) {
            const view = this.editor.current.view;

            if (view) {
                var html = EditorUtils.getHtml(view.state);

                EditorUtils.setHtml(view, "");
                if (!accentUtils.isEmpty(html) && html !== "<p></p>") {
                    this.setState({ isAddingNote: true });
                    this.addNewNote(html).then(r => {
                        return this.refreshNotes(); 
                    }).finally(() => {
                        this.setState({ isAddingNote: false });
                    });
                }

            }
        }

    }

    onHeaderExpanded() {
        if (!this.state.loaded) {
            this.refreshNotes();
        }
    }

    refreshNotes(skip = 0, take = 20) {
        var me = this;

        return this.props.loadNotes({ skip: skip, take: take, search: me.state.search }).then(function (res) {

            const newNotes = skip === 0 ? res.Notes : [...me.state.notes, ...res.Notes];

            me.setState({ notes: newNotes, show: true, loaded:true, skip: skip + (take ?? -1), count: res.Count });
        });
    }


    scrollToViewWithOffset() {
        const view = this.editor.current.view;
        const editorRect = view.dom.getBoundingClientRect();
        const desiredScrollTop = editorRect.top + window.pageYOffset - 150;

        window.scrollTo({
            top: desiredScrollTop,
            behavior: 'auto',
        });
    }

    render() {

        const me = this;

        var details = null;


        if (!this.state.loaded) {
            details = <AccentSpinner />;
        } else {

            const items = this.state.notes.map(function (note) {

                const canEdit = canEditFileNote(note);


                const jobLineDetails = accentUtils.isNull(note.JobLineID) ? undefined : <span>Line [<A href={`/JobLine/${note.JobLineID}`} >{note.JobLine}</A>]</span>;

                return (
                    <li key={note.ID}>
                        <h5>
                            <FileNoteView fileNote={note } />
                            <FileNoteTag tagName={note.Tag} />{format.formatDateTime(new Date(note.Date), true, true)} - {jobLineDetails} {accentUtils.isNull(jobLineDetails) ? '' : ' - '} {note.Representative}
                            <div style={{ fontSize: "smaller", display: "inline-block" }}>&nbsp;[<div className="acc-link" onClick={e => me.onEmail(note)} >Email To</div>]</div>
                            <div style={{ fontSize: "smaller", display: "inline-block" }}>&nbsp;<AlertStatus isActive={note.Alert ?? false} fileNoteID={ note.ID } /></div>                            
                            {canEdit && <div style={{ fontSize: "smaller", display: "inline-block" }}>&nbsp;[<div className="acc-link" onClick={e => me.onEdit(note)} >Edit</div>]</div>}
                        </h5>                        
                        <div dangerouslySetInnerHTML={{ __html: note.Notes }} />
                    </li>);
            });
            
            const tools = [
                [EditorTools.Bold, EditorTools.Italic, EditorTools.Underline, EditorTools.Strikethrough],
                [EditorTools.OrderedList, EditorTools.UnorderedList],
                [EditorTools.Undo],
                [p => <InsertSketch {...p} jobID={this.props.jobID} jobLineID={this.props.jobLineID} defaultSketchName={this.props.defaultSketchName} done={this.scrollToViewWithOffset} onInsert={this.onInsertSketch} />],
                [p => <InsertUser {...p} jobID={this.props.jobID} done={this.scrollToViewWithOffset} onInsert={this.onInsertUser} />]
            ];



            const btnStyle = { height: "37px" };
            
            const n = nodes;

            n.image.attrs = {
                ...n.image.attrs, "data-accent-doc": { default: null}
            };
            const customSchema = new Schema({
                marks: marks,
                nodes: n,                
            });

            const editing = !accentUtils.isNull(this.state.editNote);


            const canFetchNext20 = this.state.notes.length < this.state.count;
            const canFetchNext50 = this.state.notes.length < this.state.count;
            const canFetchAll = this.state.notes.length < this.state.count;

            details = accentUtils.isEmpty(this.props.disabled) ? <Row>
                <Col md={6}>
                    <Editor
                        ref={this.editor}
                        contentStyle={{ height: "300px" }}
                        defaultEditMode="div"
                        tools={tools}
                        schema={customSchema} 
                    />
                </Col>
                <Col md={6}>
                    <Row>
                        <Col md={12}>
                            <InputGroup className="trackingSearch" size="sm" >
                                {!editing && <InputGroupText>
                                    {this.state.isAddingNote ? (
                                        <LuLoaderCircle size={24} style={{ color: "blue" }} className="animate-spin" />
                                    ) : (
                                        <AccentIcons.New style={{ color: "blue", cursor: "pointer" }} onClick={this.addNote} />
                                    )}
                                </InputGroupText>}
                                {editing && <InputGroupText><AccentIcons.Save style={{ color: "blue" }} onClick={this.saveEditNote} /></InputGroupText>}
                                {editing && <InputGroupText><AccentIcons.Undo style={{ color: "blue" }} onClick={this.undoEditNote} /></InputGroupText>}                                
                                <Input key={me.searchRefreshKey} onKeyPress={me.onKeyPress} onChange={e => me.onSeachChange(e.target.value)} type="text" name="search" placeholder={t("application_strings.views.tracking.searchPlaceholder")} />
                                <InputGroupText><Button style={btnStyle} onClick={() => me.onSeachChange(null)}>X</Button></InputGroupText>
                            </InputGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} className="file-note-ctrl">
                            <ul>
                                {items}
                            </ul>
                        </Col>
                    </Row>   
                    <Row>
                        <Col md={12}>
                            <InputGroup className="trackingSearch" size="sm" >
                                <InputGroupText>{this.state.notes.length} of { this.state.count }</InputGroupText>                                
                                <InputGroupText><Button style={btnStyle} disabled={!canFetchNext20} onClick={() => this.refreshNotes(this.state.skip) }>Next 20</Button></InputGroupText>                                
                                <InputGroupText><Button style={btnStyle} disabled={!canFetchNext50} onClick={() => this.refreshNotes(this.state.skip, 50)}>Next 50</Button></InputGroupText>                                
                                <InputGroupText><Button style={btnStyle} disabled={!canFetchAll} onClick={() => this.refreshNotes(this.state.skip, null)}>All</Button></InputGroupText>                                
                            </InputGroup>
                        </Col>
                    </Row>
                </Col>
            </Row> : this.props.disabled;




        }


        const title = this.props.title ?? "application_strings.application.general.fileNotes";

        const fileNoteContent = <Col md={12}>
            {details}
        </Col>;

        const content = this.props.noHeaderSection ? <Row><h4>{t(title)}</h4>{fileNoteContent}</Row> : <AccentSectionHeader title={title} collapsed={true} onExpand={this.onHeaderExpanded} onIsExpandedOnLoad={this.onHeaderExpanded}>
            {fileNoteContent }
        </AccentSectionHeader>;
        
        return content;

    }

}




export const FileNoteAlert = ({ contactID, companyID, top, color = "blue" }) => {

    const fileNoteAlertContext = useFileNoteAlerts();

    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const [alerts, setAlerts] = React.useState(null);
    const ctrl = React.useRef(null);

    React.useEffect(() => {


        if (fileNoteAlertContext.hasAlertsForContact(contactID)) {
            setAlerts(fileNoteAlertContext.getAlertsForContact(contactID));

            return;
        }

        if (fileNoteAlertContext.hasAlertsForCompany(companyID)) {
            setAlerts(fileNoteAlertContext.getAlertsForContact(companyID));
            return;
        }


        query_FileNoteAlertsForContactCompany.getAll({ contactID: contactID, companyID: companyID }).then(alerts => {

            setAlerts(alerts);

            fileNoteAlertContext.registerFileNoteAlerts(alerts);

            if (!accentUtils.isNull(contactID)) {
                fileNoteAlertContext.contactViewed(contactID);
            }
            if (!accentUtils.isNull(companyID)) {
                fileNoteAlertContext.companyViewed(companyID);
            }
            

        });


    }, []);

    const toggle = () => {
        setPopoverOpen(true);
    };

    const close = () => {
        setPopoverOpen(false);
    };


    if (accentUtils.isEmpty(alerts)) {
        return null;
    }

    const style = { color: color };

    const clickElement = <div className="d-flex">
        <AccentIcons.BellActive style={style} ref={ctrl} onClick={toggle} top={ top} />        
    </div>;



    return (
        <div>
            {clickElement}
            <AccentPopup
                className="jobWatcherPopup"
                popupAlign={{ horizontal: "left" }}
                anchorAlign={{ horizontal: "right", vertical: "bottom" }}
                show={popoverOpen}
                onAutoClose={close}
                anchor={ctrl}
                headerContent={
                    <div className="d-flex">
                        <div className="me-auto"><strong>ALERTS</strong></div>
                        <div><AccentIcons.Close className="acc-btn" onClick={close} noPadding /></div>
                    </div>
                }                
            >
                <AlertsDisplay alerts={ alerts} />
            </AccentPopup>
        </div>
    );

};


const AlertStatus = ({ isActive: externalIsActive, fileNoteID, size = 24 }) => {
    const [internalIsActive, setInternalIsActive] = React.useState(externalIsActive || false);
    const [isUpdating, setIsUpdating] = React.useState(false);

    React.useEffect(() => {
        if (externalIsActive !== undefined) {
            setInternalIsActive(externalIsActive);
        }
    }, [externalIsActive]);

    const handleClick = async () => {
        const newState = !internalIsActive;
        setIsUpdating(true);
        try {
            setInternalIsActive(newState);
            await update("FileNote_SetAlert", { ID: fileNoteID, Alert: newState });
        } catch (error) {
            // Revert state if update fails
            setInternalIsActive(!newState);
            console.error('Failed to update alert status:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    if (isUpdating) {
        return (
            <LuLoaderCircle
                size={size}
                style={{ color: "blue", cursor: "wait" }}
                strokeWidth={2}
                className="animate-spin"
            />
        );
    }

    if (internalIsActive) {
        return (
            <LuBellRing
                size={size}
                style={{ color: "red", cursor: "pointer" }}
                strokeWidth={2}
                onClick={handleClick}
            />
        );
    }

    return (
        <LuBellOff
            size={size}
            style={{ color: "grey", cursor: "pointer" }}
            strokeWidth={2}
            onClick={handleClick}
        />
    );
};
